import { Box, Divider, MenuItem } from '@mui/material';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  selectIsAdmin,
  selectOrganizationName,
  selectOrganizations,
  setActiveOrganizationId,
  selectActiveOrganizationId,
  selectIsDisabled,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { UserManagementIcon } from '@/shared/ui';
import { BottomItem, BottomMenuItem } from '../BottomItem';
import { ORG_COLORS, OrgAvatar } from './OrgAvatar';
import { OrgIcon } from './OrgIcon';

export function OrgBottomItem() {
  const dispatch = useAppDispatch();
  const isUserDisabled = useAppSelector(selectIsDisabled);
  const organizationName = useAppSelector(selectOrganizationName);
  const organizations = useAppSelector(selectOrganizations);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const isAdmin = useAppSelector(selectIsAdmin);

  const sortedOrganizations = useMemo(() => {
    return organizations?.toSorted((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );
  }, [organizations]);

  const handleChangeOrganization = (organizationId: string) => () => {
    dispatch(setActiveOrganizationId(organizationId));
  };

  const orgLabel = organizationName ?? '';

  const menuItems = [
    ...(sortedOrganizations.length > 1
      ? [
          <Box
            key="organizations-list"
            sx={{
              mx: -1,
              mt: -1,
              p: 1,
              maxHeight: 180,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {...sortedOrganizations.map((organization, organizationIndex) => (
              <MenuItem
                key={organization.id}
                selected={activeOrganizationId === organization.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderRadius: 2,
                  p: 1,
                }}
                onClick={handleChangeOrganization(organization.id)}
              >
                <OrgAvatar
                  name={organization.name}
                  color={ORG_COLORS[organizationIndex % ORG_COLORS.length]}
                />
                {organization.name}
              </MenuItem>
            ))}
          </Box>,
          <Divider key="organizations-divider" sx={{ mx: -1, mb: 1 }} />,
        ]
      : []),
    ...(!isUserDisabled
      ? [
          <BottomMenuItem
            key="organization-info"
            icon={<OrgIcon />}
            label="Organization Info"
            component={Link}
            to={ROUTES.organizationInfo}
          />,
        ]
      : []),
    ...(isAdmin
      ? [
          <BottomMenuItem
            key="user-management"
            icon={<UserManagementIcon />}
            label="User Management"
            component={Link}
            to={ROUTES.userManagement}
          />,
        ]
      : []),
  ];

  return (
    <BottomItem
      icon={<OrgAvatar name={orgLabel} />}
      label={orgLabel}
      menuContent={menuItems}
    />
  );
}
